import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';
import { List, Map } from 'immutable';

import AgGridTable from '../../helpers/AgGridTable';
import Button from '../../helpers/Button';
import { ContextMenuCellRenderer } from '../../helpers/ContextMenuCellRenderer';

import style from './style.module.css';

const REMOVE_2FA_FIELD = 'remove2Fa';
const gridStyle = { height: '100%' };

const CONTEXT_ACTIONS = List([
  Map({
    key: 'edit',
    name: (
      <>
        <i className="fa fa-pencil" /> Edit
      </>
    ),
  }),
]);

/**
 * Cell renderer of the "Inactive" column.
 *
 * @param {object} props AG-Grid cell renderer props.
 * @param {boolean} props.value Inactive state of the user.
 */
const InactiveCellRendererComponent = props => {
  const { value } = props;

  return (
    <ContextMenuCellRenderer {...props}>
      <input checked={Boolean(value)} type="checkbox" disabled />
    </ContextMenuCellRenderer>
  );
};

InactiveCellRendererComponent.propTypes = {
  value: PropTypes.bool,
};

const InactiveCellRenderer = memo(InactiveCellRendererComponent);

/**
 * Cell renderer of the "Remove 2FA" column.
 *
 * @param {object} props Props.
 * @param {object} props.data Row data.
 * @param {Function} props.onRemove2FaClick Handle remove 2FA click, called with the user id.
 */
const Remove2FaCellRendererComponent = ({ data, onRemove2FaClick }) => {
  const handleClick = useCallback(() => {
    onRemove2FaClick(data.id);
  }, [data.id, onRemove2FaClick]);

  return (
    <Button className="btn-danger" onClick={handleClick}>
      Remove 2FA
    </Button>
  );
};

Remove2FaCellRendererComponent.propTypes = {
  data: PropTypes.object.isRequired,
  onRemove2FaClick: PropTypes.func.isRequired,
};

const Remove2FaCellRenderer = memo(Remove2FaCellRendererComponent);

/**
 * User table component.
 *
 * @param {object} props Props.
 * @param {Immutable.List} props.tableCells Table columns configuration.
 * @param {Immutable.List} props.users List of users needed to display in the table.
 * @param {boolean} props.userLoading If set to true will show the loading spinner.
 * @param {Function} props.onEdit Handle the "Edit" context menu item, called with the user id.
 * @param {Function} props.onRemove2FaClick Handle remove 2FA click, called with the user id.
 * @param {Function} props.onSortChange Handle sort change, called with `{ sort, sortOrder }`.
 */
const UserTableComponent = ({ tableCells, users, userLoading, onEdit, onRemove2FaClick, onSortChange }) => {
  const columnDefs = useMemo(
    () =>
      tableCells.map(column => {
        const field = column.get('field');
        const columnDef = Map({
          headerName: column.get('title'),
          field,
          flex: column.get('width'),
          sortable: column.has('sortField'),
        });

        if (field === 'inactive') {
          return columnDef.merge({ cellClass: style.centerCell, cellRendererFramework: InactiveCellRenderer });
        }

        if (field === REMOVE_2FA_FIELD) {
          return columnDef.merge({
            cellRendererFramework: Remove2FaCellRenderer,
            cellRendererParams: { onRemove2FaClick },
          });
        }

        return columnDef.set('cellRendererFramework', ContextMenuCellRenderer);
      }),
    [tableCells, onRemove2FaClick],
  );

  const handleSortModelChanged = useCallback(
    sortModel => {
      const [sortedColumn] = sortModel;
      const sortField = sortedColumn
        ? tableCells.find(column => column.get('field') === sortedColumn.colId).get('sortField')
        : '';

      onSortChange({ sort: sortField, sortOrder: sortedColumn ? sortedColumn.sort : '' });
    },
    [tableCells, onSortChange],
  );

  const handleContextMenuClick = useCallback(
    (event, { key, rowData }) => {
      if (key === 'edit') {
        onEdit(rowData.id);
      }
    },
    [onEdit],
  );

  return (
    <div className={style.host}>
      <AgGridTable
        columnDefs={columnDefs}
        contextActionList={CONTEXT_ACTIONS}
        isFetching={userLoading}
        onContextMenuClick={handleContextMenuClick}
        onSortModelChanged={handleSortModelChanged}
        rowData={users}
        style={gridStyle}
        serverSideSorting
        sortable
      />
    </div>
  );
};

UserTableComponent.propTypes = {
  onEdit: PropTypes.func.isRequired,
  onRemove2FaClick: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  tableCells: PropTypes.instanceOf(List).isRequired,
  userLoading: PropTypes.bool,
  users: PropTypes.instanceOf(List).isRequired,
};

export const UserTable = memo(UserTableComponent);
